import { useApp } from '@/App';
import FleetTitle from '@/libs/antd/components/Title';
import DataRow, { DataRowItem } from '@/libs/antd/components/DataRow';
import {
	ActivityRoboticStatus,
	UpdateFeedbackPayload,
	useActivityRoboticDetail,
	useCancelAppId,
	useRetryRoboticStep,
	useReturnToQC,
	useUpdateActivityFeedback,
} from '@/libs/fleet-api/services/robotic';
import CancelConfirmModal from '@/pages/robotic/components/CancelConfirmModal';
import EditPendingFieldModal from '@/pages/robotic/components/EditPendingFieldModal';
import FeedbackHistoryTable from '@/pages/robotic/components/FeedbackHistoryTable';
import ReturnConfirmModal from '@/pages/robotic/components/ReturnConfirmModal';
import ViewDetailModal from '@/pages/robotic/components/ViewDetailModal';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Modal, Select, Spin, notification } from 'antd';
import dayjs from 'dayjs';
import { FC, useState } from 'react';

type ActivityRoboticDetailPageProps = {
	purchaseOrderNo: string;
	status: ActivityRoboticStatus;
	onBack?: () => void;
};

const ActivityRoboticDetailPage: FC<ActivityRoboticDetailPageProps> = ({
	purchaseOrderNo,
	status,
	onBack,
}) => {
	const { navigate } = useApp();
	const queryClient = useQueryClient();

	const detailQuery = useActivityRoboticDetail({ purchaseOrderNo });
	const detail = detailQuery.data?.data;
	const isLoading = detailQuery.isLoading;

	const returnToQCMutation = useReturnToQC();
	const cancelAppIdMutation = useCancelAppId();
	const updateFeedbackMutation = useUpdateActivityFeedback();
	const retryRoboticStepMutation = useRetryRoboticStep();

	const [returnModalOpen, setReturnModalOpen] = useState(false);
	const [cancelModalOpen, setCancelModalOpen] = useState(false);
	const [viewDetailItemId, setViewDetailItemId] = useState<number | null>(null);
	const [editItemId, setEditItemId] = useState<number | null>(null);
	const [updateModule, setUpdateModule] = useState<string | undefined>(undefined);

	const handleBack = () => {
		if (typeof onBack === 'function') {
			onBack();
		} else {
			navigate('ActivityRoboticPraPencairanListPage' as never);
		}
	};

	const handleReturnConfirm = (notes: string) => {
		returnToQCMutation.mutate(
			{ purchaseOrderNo, notes },
			{
				onSuccess: () => {
					notification.success({
						message: 'Berhasil',
						description: 'Data berhasil di-return ke Modul Quality Control.',
					});
					setReturnModalOpen(false);
					handleBack();
				},
			},
		);
	};

	const handleCancelConfirm = (notes: string) => {
		cancelAppIdMutation.mutate(
			{ purchaseOrderNo, notes },
			{
				onSuccess: () => {
					notification.success({
						message: 'Berhasil',
						description: 'Application Data berhasil dibatalkan.',
					});
					setCancelModalOpen(false);
					handleBack();
				},
			},
		);
	};

	const handleEditSubmit = (field: string, value: string) => {
		if (editItemId == null) return;
		const payload: UpdateFeedbackPayload = {
			roboticActivityId: editItemId,
			field,
			value,
		};
		updateFeedbackMutation.mutate(payload, {
			onSuccess: () => {
				notification.success({
					message: 'Berhasil',
					description: 'Pending field berhasil diupdate.',
				});
				setEditItemId(null);
				queryClient.invalidateQueries({ queryKey: ['getActivityRoboticHistories', { purchaseOrderNo }] });
			},
		});
	};

	const handleRetry = (activityDetailId: number) => {
		Modal.confirm({
			title: 'Konfirmasi Retry',
			content: 'Apakah Anda yakin untuk melakukan retry step ini?',
			okText: 'Ya',
			cancelText: 'Tidak',
			onOk: () => {
				retryRoboticStepMutation.mutate(activityDetailId, {
					onSuccess: () => {
						notification.success({
							message: 'Berhasil',
							description: 'Retry step berhasil diproses.',
						});
						queryClient.invalidateQueries({ queryKey: ['getActivityRoboticHistories', { purchaseOrderNo }] });
					},
				});
			},
		});
	};

	const mainInfo = detail?.mainInfo;

	const infoItems: DataRowItem[] = [
		[
			['Nama Customer', mainInfo?.customerName ? (
				<a href='#' onClick={(e) => { e.preventDefault(); navigate('CustomerPageV2' as never, { customerId: mainInfo.customerId } as never); }}>
					{mainInfo.customerName}
				</a>
			) : '-'],
			['Asset', mainInfo?.assetDescription || '-'],
		],
		[
			['FRM', mainInfo?.frmName || '-'],
			['Group BMPP', mainInfo?.groupBmppName ? (
				<a href='#' onClick={(e) => { e.preventDefault(); navigate('FRMCustomerGroupListPage' as never); }}>
					{mainInfo.groupBmppName}
				</a>
			) : '-'],
		],
		[
			['FBDH', mainInfo?.fbdhName || '-'],
			['Tanggal RKP', mainInfo?.rkpDate ? dayjs(mainInfo.rkpDate).format('DD/MM/YYYY') : '-'],
		],
		[
			['Sumber Order', mainInfo?.sourceOrder || '-'],
			['Tanggal Risalah Full Approved', mainInfo?.resolutionFullApprovedDate ? dayjs(mainInfo.resolutionFullApprovedDate).format('DD/MM/YYYY') : '-'],
		],
		[
			['Jenis Fasilitas', mainInfo?.facilityType || '-'],
			undefined,
		],
	];

	const feedbackItems = detail?.histories ?? [];

	// canCancel/canReturn: fall back to status-based logic if API doesn't return these
	const canCancel = detail?.canCancel ?? (status === 'Pending');
	const canReturn = detail?.canReturn ?? (status === 'Waiting' || status === 'Pending');

	if (isLoading) {
		return (
			<div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
				<Spin size='large' />
			</div>
		);
	}

	const isReturning = returnToQCMutation.isPending;
	const isCancelling = cancelAppIdMutation.isPending;

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
			{/* Header */}
			<div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
				<Button
					icon={<ArrowLeftOutlined />}
					style={{ width: 44, height: 44, background: '#1557BC', color: '#fff' }}
					onClick={handleBack}
				/>
				<FleetTitle marginBottom={0} style={{ flex: 1 }}>
					Detail Activity Robotic
				</FleetTitle>
			</div>

			{/* Main Information */}
			<DataRow items={infoItems} loading={isLoading} />

			{/* Feedback History */}
			<FleetTitle level={3} marginBottom={0}>
				History Aktivitas / Feedback
			</FleetTitle>

			<FeedbackHistoryTable
				feedbackItems={feedbackItems}
				status={status}
				onViewDetail={(id) => setViewDetailItemId(id)}
				onEdit={(id) => setEditItemId(id)}
				onRetry={(id) => handleRetry(id)}
			/>

			{/* Update Data section (Finish status only) */}
			{status === 'Finish' && (
				<div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
					<FleetTitle level={3} marginBottom={0}>
						Update Data
					</FleetTitle>
					<div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
						<Select
							placeholder='Pilih modul'
							value={updateModule}
							onChange={(v) => setUpdateModule(v)}
							style={{ width: 240 }}
							options={[
								{ label: 'Customer Data', value: 'Customer Data' },
								{ label: 'Application Data', value: 'Application Data' },
								{ label: 'Asset Data', value: 'Asset Data' },
								{ label: 'Financial Data', value: 'Financial Data' },
								{ label: 'Insurance Data', value: 'Insurance Data' },
								{ label: 'Incentive Data', value: 'Incentive Data' },
							]}
						/>
						<Button
							type='primary'
							disabled={!updateModule}
							onClick={() => {
								/* TODO: confirm update-data endpoint with BE */
								notification.info({ message: 'Endpoint update-data belum dikonfirmasi BE.' });
							}}
						>
							Submit
						</Button>
					</div>
				</div>
			)}

			{/* Footer Buttons */}
			<div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 30 }}>
				{canCancel && (
					<Button
						danger
						loading={isCancelling}
						disabled={isReturning}
						onClick={() => setCancelModalOpen(true)}
					>
						Batalkan
					</Button>
				)}
				{canReturn && (
					<Button
						loading={isReturning}
						disabled={isCancelling}
						onClick={() => setReturnModalOpen(true)}
					>
						Return ke QC
					</Button>
				)}
				<Button onClick={handleBack}>
					Kembali
				</Button>
			</div>

			{/* Modals */}
			<ReturnConfirmModal
				open={returnModalOpen}
				onConfirm={handleReturnConfirm}
				onCancel={() => setReturnModalOpen(false)}
				loading={isReturning}
			/>

			<CancelConfirmModal
				open={cancelModalOpen}
				onConfirm={handleCancelConfirm}
				onCancel={() => setCancelModalOpen(false)}
				loading={isCancelling}
			/>

			<EditPendingFieldModal
				open={editItemId !== null}
				activityDetailId={editItemId}
				onSubmit={handleEditSubmit}
				onCancel={() => setEditItemId(null)}
				loading={updateFeedbackMutation.isPending}
			/>

			<ViewDetailModal
				open={viewDetailItemId !== null}
				activityDetailId={viewDetailItemId}
				onClose={() => setViewDetailItemId(null)}
			/>
		</div>
	);
};

export default ActivityRoboticDetailPage;
